<template>
  <div>
    <section v-if="finishedLoading" class="health-status-container">
      <profile-card class="mb-3"></profile-card>
      <section class="health-overview-container p-1">
        <h3 class="health-overview-title p-1">{{ $t('healthStatus.title') }}</h3>
        <!-- Not Reported Card -->
        <base-card class="reported-info p-2" v-if="reportedHealthStatus === 'none'">
          <font-awesome-icon :icon="['far', 'comment-alt-slash']" class="icon mx-auto d-block mt-2"></font-awesome-icon>
          <h5 class="health-card-title text-center mt-2">{{ $t('healthStatus.notReported.title') }}</h5>
          <p class="health-card-description text-center">{{ $t('healthStatus.notReported.description') }}</p>
        </base-card>
        <!-- Bad Card -->
        <base-card class="reported-error p-2" v-else-if="reportedHealthStatus === 'error'">
          <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="icon mx-auto d-block mt-2"></font-awesome-icon>
          <h5 class="health-card-title text-center mt-2 mb-0">{{ getDay(getLastHealthCheckDate()) }}</h5>
          <p class="health-card-description text-center">{{ formatDate(getLastHealthCheckDate()) }}</p>
        </base-card>
        <!-- Good Card -->
        <base-card class="reported-success p-2" v-else>
          <font-awesome-icon :icon="['fas', 'check-circle']" class="icon mx-auto d-block mt-2"></font-awesome-icon>
          <h5 class="health-card-title text-center mt-2 mb-0">{{ getDay(getLastHealthCheckDate()) }}</h5>
          <p class="health-card-description text-center">{{ formatDate(getLastHealthCheckDate()) }}</p>
        </base-card>
        <!-- Generic -->
        <router-link tag="button" class="mx-auto d-block p-2 mt-2 mb-2 update-health-status-button" :to="{ name: 'UpdateHealthStatusPage' }">
          {{ $t('healthStatus.healthStatusUpdateButton') }}
        </router-link>
        <p class="mb-2">{{ $t('healthStatus.disclaimer') }}</p>
        <p class="last-check" v-if="healthStatus">{{ $t('healthStatus.lastCheck') }}:
          {{ formatDate(getLastHealthCheckDate()) }}</p>
        <!-- List of Symptom Checks -->
        <ul class="health-check-list-container">
          <li v-for="(answer, index) in selectedAnswers" :key="`health-status-answer-${index}`" class="health-check-list-item d-block p-2">
            <div class="reported-card p-2" v-if="failedHealthCheckQuestion(answer)">
              <p class="content p-0 m-0">
                <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="health-icon"></font-awesome-icon>
                {{ answer }}
              </p>
            </div>
            <div class="health-icon-container p-1" v-else>
              <p class="content p-0 m-0">
                <font-awesome-icon :icon="['fas', 'check-circle']" class="health-icon mr-1"></font-awesome-icon>
                {{ answer }}
              </p>
            </div>
          </li>
        </ul>
      </section>
    </section>
    <loading-spinner v-else />
  </div>
</template>

<script>
import { format } from 'date-fns';
import {mapMutations, mapState} from 'vuex';
import common from "@/mixins/common";
import UserService from '@/services/user.service';
import MemberService from '@/services/member.service';

export default {
  name: 'HealthStatusPage',
  mixins: [common],
  components: {
    ProfileCard: () => import('@/components/profile-card/profile-card.vue'),
    BaseCard: () => import('@/components/base-card/base-card.vue'),
    LoadingSpinner: () => import('@/components/loading-spinner/loading-spinner'),
  },
  data() {
    return {
      badAnswers: Object.freeze([
        'I have one or more COVID-19 symptoms, or I have tested positive for COVID-19 in the past 10 days.',
        'I have been in contact with someone who has COVID-19 or COVID-19 symptoms.',
        'I have, or may have, traveled in a high-risk area.',
        'I have been in contact with someone who has COVID-19 or COVID-19 symptoms and I am not fully vaccinated.',
        'I have, or may have, traveled internationally or to a high-risk destination and I am not fully vaccinated.',
      ]),
      healthStatus: null,
      organizationMember: null,
    };
  },
  beforeMount() {
    this.initialLoad();
  },
  beforeDestroy() {
  },
  computed: {
    ...mapState(['currentHealthStatus', 'currentUser', 'defaultOrganization', 'currentMember']),
    currentCompletables() {
      if (this.healthStatus) {
        return this.healthStatus.attributes.data.completables;
      }
      return [];
    },
    selectedAnswers() {
      return this.currentCompletables.map((complete) => {
        const answer = complete.answers.find((ans) => ans.selected === true);
        if (answer) {
          return answer.body.en;
        }
        return null;
      });
    },
    reportedHealthStatus() {
      let status = 'none';
      if (this.organizationMember && this.organizationMember.attributes) {
        let healthCheckScore = parseInt(this.organizationMember.attributes.last_health_check_score, 10);
        this.log('HealthStatus -> reportedHealthStatus', 'healthCheckScore', healthCheckScore);
        if (healthCheckScore === 1337) {
          status = 'none';
        } else if (healthCheckScore === 3) {
          status = 'success';
        } else if (healthCheckScore < 3) {
          status = 'error';
        }
      }
      return status;
    },
  },
  methods: {
    ...mapMutations(['setCurrentHealthStatus']),
    reset() {
      this.finishedLoading = false;
      this.healthStatus = null;
      this.organizationMember = null;
    },
    initialLoad() {
      this.reset();
      this.healthStatus = this.currentHealthStatus;
      this.log('HealthStatus -> beforeMount', 'currentUser', this.currentUser);
      this.log('HealthStatus -> beforeMount', 'defaultOrganization', this.defaultOrganization);
      this.log('HealthStatus -> initialLoad', 'currentMember', this.currentMember);
      const organizationId = this.defaultOrganization.data.id;
      const usedId = this.currentUser.data.id;
      const secondsSinceEpoch = Math.round(Date.now() / 1000);
      const params = {timestamp: secondsSinceEpoch};
      UserService.healthStatus(usedId, organizationId).then(({ data }) =>{
        this.log('HealthStatus -> initialLoad -> UserService.healthStatus', 'data', data);
        if (data) {
          this.healthStatus = data;
          this.setCurrentHealthStatus(data);
        }
        this.finishedLoading = true;
      });
      const memberId = this.currentMember.id;
      this.log('HealthStatus -> initialLoad', 'memberId', memberId);
      MemberService.show(memberId, params).then(({ data }) => {
        this.log('HealthStatus -> initialLoad -> MemberService.show', 'data', data);
        if (data) {
          this.organizationMember = data;
        }
      });
    },
    getLastHealthCheckDate() {
      let date = null;
      if (this.currentMember && this.currentMember.attributes) {
        date = this.currentMember.attributes.last_health_check_date;
      }
      // this.log('HealthStatus -> getLastHealthCheckDate', 'date', date);
      return date;
    },
    formatDate(date) {
      let formattedDate = null;
      if (date) {
        try {
          formattedDate = format(new Date(date), 'MM/dd/yy hh:mm a');
        } catch (e) {
          this.logWarning('HealthStatus -> formatDate -> error formatting date', 'error', {
            error: e,
            date: date,
          });
        }
      }
      return formattedDate;
    },
    getDay(date) {
      let formattedDate = null;
      if (date) {
        try {
          formattedDate = format(new Date(date), 'EEEE');
        } catch (e) {
          this.logWarning('HealthStatus -> getDay -> error formatting date', 'error', {
            error: e,
            date: date,
          });
        }
      }
      return formattedDate;
    },
    failedHealthCheckQuestion(ans) {
      let result = false;
      if (ans) {
        for (let i = 0; i < this.badAnswers.length; i += 1) {
          if (ans === this.badAnswers[i]) {
            result = true;
            break;
          }
        }
      }
      return result;
    },
  },
};
</script>

<style lang="scss" scoped src="./styles.scss">

</style>
